import XDate from 'xdate';
import {
  parse_us_phone_number as parseUSPhoneNumber,
  valid_email as validEmail,
} from '~/common/utils';

export const selectDetails = ({details}) => details;
export const selectRequestFailed = ({details}) => details.request.failed;

/** Required fields: [host, start, title, type] */
export const selectTitle = ({details}) => details.title.trim();
export const selectType = ({details, ecard}) => details?.type ?? ecard?.event_data?.event_type;
export const selectHost = ({details, ecard}) => details?.host ?? ecard?.event_data?.event_host_name;
export const selectStart = ({details}) => details.start;
export const selectRsvpOff = ({details}) => details.rsvp_off;
export const selectDowngraded = ({details}) => details.downgraded;
export const selectHasGuestsOfHonor = ({details}) =>
  details?.guestsOfHonor?.guests && details.guestsOfHonor.guests.length > 0;

export const selectEventDateHasPassed = ({details}) => {
  const {start} = details;

  const then = new XDate(start);
  const now = new XDate();
  const diff = now.diffDays(then);

  if (diff < 0) return true;
  return false;
};

export const selectTitleError = ({details}) =>
  selectRequestFailed({details}) && !selectTitle({details});
export const selectTypeError = ({details}) =>
  selectRequestFailed({details}) && !selectType({details});
export const selectHostError = ({details}) =>
  selectRequestFailed({details}) && !selectHost({details});
export const selectStartDateError = ({details}) =>
  selectRequestFailed({details}) &&
  (!selectStart({details}) || selectEventDateHasPassed({details}));
export const selectRequiredComplete = ({details}) =>
  details.title.trim() && details.type && details.host && details.start;

/** Optional fields: [co-host, end] */
export const selectCoHost = ({details}) => details?.cohost;
export const selectCohostName = ({details}) => details.cohost.name;
export const selectCohostEmail = ({details}) => details.cohost.email;

export const selectCohostEmailValid = ({details}) =>
  validEmail(selectCohostEmail({details})).is_valid;
export const selectCohostNameError = ({details}) =>
  selectCohostEmailValid({details}) && !selectCohostName({details});
const selectCohostEmailMissing = ({details}) =>
  selectCohostName({details}) && !selectCohostEmailValid({details});
export const selectValidCohost = ({details}) =>
  !selectCohostEmailMissing({details}) && !selectCohostNameError({details});
export const selectGuestsOfHonor = ({details}) => {
  if (details.guestsOfHonor.guests.length === 0) return [{name: '', email: ''}];
  return details.guestsOfHonor.guests;
};
export const selectIsFuneral = ({details}) => selectType({details}) === 'funeral_memorial_service';
export const selectPhoneNumber = ({details}) => String(details.phoneNumber);
export const selectPhoneNumberIsValid = ({details}) =>
  !selectPhoneNumber({details}) ||
  (selectPhoneNumber({details}) && parseUSPhoneNumber(selectPhoneNumber({details})).is_valid);

export const selectStartDateBeforeEndDate = ({details}) => {
  const {start, end} = details;
  if (!end) return false;
  return new Date(end).getTime() - new Date(start).getTime() < 0;
};

/** Allow submission */
export const selectIsDirty = ({details}) => {
  const {address, cohost, time, timezone, request, ...rest} = details;

  const flattened = {...address, ...cohost, ...time, ...rest};
  const id = (i) => i;

  return !Object.values(flattened).some(id);
};

/** Validate on submit/navigation */
export const selectEventDetailsValid = ({details}) => {
  if (selectEventDateHasPassed({details})) return false;
  if (selectStartDateBeforeEndDate({details})) return false;
  if (!selectValidCohost({details})) return false;
  if (!selectPhoneNumberIsValid({details})) return false;
  return selectRequiredComplete({details});
};

export const eventIsPremium = ({details, ecard}) => details?.isPremium ?? !!ecard; // eCards are always premium
export const eventInEditMode = ({details, ecard}) => details?.inEditMode ?? ecard.isInEditMode;
export const eventInUpsellMode = ({details}) => details.isUpsellMode;
export const selectUsedAs = ({details}) => details.usedAs;
export const selectIsDetailsPristine = ({details}) => details.pristine;
